import styled from 'styled-components';
import { Markdown } from 'vendor/mods/markdown/components/Markdown';
import { Paragraph } from 'src/components/common/typography/Text';
import {
  ProductAttributeCode,
  ProductAttributeCollection,
} from 'src/models/pim/attributes';
import { useProductAttributes } from 'src/modules/products/hooks/useProductAttributes';
import { FC } from 'react';
import { DictionaryTitlesCode } from 'src/models/pim/dictionaries';
import { ProductDTO } from 'src/models/pim';
import { useTitlesDictionary } from 'src/modules/products/hooks/useTitlesDictionary';
import { useTranslateItem } from 'src/hooks/directus/useTranslateItem';
import { transparentize } from 'polished';
import { StyleProps } from '@summer/jst-react';
import { useTranslate } from 'react-t7e';

export interface ProductPageShowcaseAttributeProps {
  product: ProductDTO;
  label?: string;
  description?: string;
  labelCode?: DictionaryTitlesCode;
  descriptionCode?: ProductAttributeCode;
}

const Container = styled.div`
  padding: 16px 0;
  width: 100%;

  color: ${({ theme }) => theme.text.palette.primary};
  border-bottom: 1rpx solid
    ${({ theme }) => transparentize(0.6, theme.text.palette.secondary)};
`;

const Label = styled(Paragraph).attrs({ as: 'span' })`
  font-weight: 500;
  text-transform: uppercase;
`;

const Description = styled(Paragraph)`
  font-size: 12px;
  em {
    display: block;
    margin-top: 8px;
  }
`;

export const ProductPageShowcaseAttribute: FC<
  ProductPageShowcaseAttributeProps & StyleProps
> = ({ product, label, labelCode, description, descriptionCode, ...props }) => {
  const { _ } = useTranslate();
  const translateFn = useTranslateItem();

  const { getProductAttribute } = useProductAttributes(product.attributes);

  const [dictionaryLabel] = useTitlesDictionary(labelCode);

  const descriptionAttribute = getProductAttribute(
    descriptionCode as unknown as ProductAttributeCollection.TextValues,
  );

  const applicationTimes = getProductAttribute(
    ProductAttributeCode.ApplicationTime,
  )?.map(translateFn);

  const labelToDisplay = label ?? dictionaryLabel?.value;

  const descriptionToDisplay =
    description ??
    descriptionAttribute
      ?.map(translateFn)
      .map((d) => d.value)
      .join(', ');

  if (!labelToDisplay || (!label && !descriptionToDisplay)) {
    return null;
  }

  return (
    <Container {...props}>
      <Label>
        <Markdown>{labelToDisplay}</Markdown>
      </Label>

      {descriptionToDisplay && (
        <Description>
          <Markdown>{descriptionToDisplay}</Markdown>

          {ProductAttributeCode.Application === descriptionCode && (
            <em>
              {applicationTimes?.map((d) => d.value).join(` ${_('and')} `)}
            </em>
          )}
        </Description>
      )}
    </Container>
  );
};
